// packages/web-ui/src/state/store.ts
//
// W2-S2 — root zustand store. Currently composes only the `navigation` slice.
// W2-S4 — extended with the tab list. The slice's `restoreTabs()` is invoked
// once at store-creation time so persisted tabs are already hydrated before
// the first React render.
// W2-S5 — composes the `chat` and `sse` slices for the React chat panel.
// W2-S6-integration — composes the `inline-cards` slice and wires
//   `chat.attachCardToMessage` to its `attachCard` action so SSE handlers
//   can push card envelopes through the chat slice without importing the
//   inline-cards store directly.
// W4 fix-pass HIGH-A (CSC-3) — composes `changeset`, `views`, `kb`, and
//   `diff` slices into the root store. Before this composition each W4
//   component built a module-level singleton via `getOrCreateDefaultStore()`
//   and the SSE dispatcher could not fan events into the W4 slices. After
//   the composition, the SPA has a single source of truth for changeset
//   state, kb-browser state, views state, and diff list state.
//
// Migration final fix-pass — HIGH-3 (UtilityDrawer Activity / Debug tabs).
//   Composes the new `eventLog` slice. The SSE dispatcher (`sse/attach-to-
//   chat-slice.ts`) now appends each event into the eventLog alongside the
//   existing per-slice fan-out so the Activity tab can render a live log
//   and the Debug tab can summarise recent activity. ADDITIVE — every
//   pre-existing slice entry below stays untouched.
//
// Subsequent stories (W2-S8 sessions, etc.) will compose additional slices
// into this store via the slice-pattern.

import { create } from 'zustand';
import type { NavigationSlice } from './navigation.js';
import { NAVIGATION_INITIAL_STATE, createNavigationSlice } from './navigation.js';
import type { ChatSlice } from './chat.js';
import { CHAT_INITIAL_STATE, createChatSlice } from './chat.js';
import type { SseSlice } from './sse.js';
import { SSE_INITIAL_STATE, createSseSlice } from './sse.js';
import type { InlineCardsSlice, InlineCardEnvelope } from './inline-cards.js';
import { INLINE_CARDS_INITIAL_STATE, createInlineCardsSlice } from './inline-cards.js';
import type { ChangesetSlice } from './changeset.js';
import { CHANGESET_INITIAL_STATE, createChangesetSlice } from './changeset.js';
import type { ViewsSlice } from './views.js';
import { VIEWS_INITIAL_STATE, createViewsSlice } from './views.js';
import type { KbSlice } from './kb.js';
import { KB_INITIAL_STATE, createKbSlice } from './kb.js';
import type { DiffSlice } from './diff.js';
import { DIFF_INITIAL_STATE, createDiffSlice } from './diff.js';
import type { EventLogSlice } from './eventLog.js';
import { EVENT_LOG_INITIAL_STATE, createEventLogSlice } from './eventLog.js';
import type { GitSlice } from './git.js';
import { GIT_INITIAL_STATE, createGitSlice } from './git.js';
import type { ImageViewsSlice } from './image-views.js';
import { IMAGE_VIEWS_INITIAL_STATE, createImageViewsSlice } from './image-views.js';
import { _resetTabsPersistForTests } from './tabs-persistence.js';

/** Root store type — extended via intersection as further slices are composed. */
export type RootStore = NavigationSlice &
  ChatSlice &
  SseSlice &
  InlineCardsSlice &
  ChangesetSlice &
  ViewsSlice &
  KbSlice &
  DiffSlice &
  EventLogSlice &
  GitSlice &
  ImageViewsSlice;

/**
 * The singleton hook used by React components to subscribe to root-store state.
 * Tests that need isolation should construct standalone slices via
 * `createStore(createNavigationSlice)` from `zustand/vanilla` instead, OR call
 * `_resetRootStoreForTests()` in `beforeEach`.
 *
 * The inline-cards delegate is bound via a closure over `useRootStore.getState`
 * so the chat slice can route `attachCardToMessage` to the inline-cards
 * slice's `attachCard` action. The `useRootStore` initializer cannot reference
 * itself directly (TDZ), so we read the slice off the live state inside the
 * delegate — by the time the delegate is invoked, the store is fully built.
 *
 * MED-2 polish — every composed slice now exposes a uniquely-prefixed reset
 * action (`_resetInlineCards`, `_resetChangeset`, `_resetViews`, `_resetKb`,
 * `_resetDiff`, `_resetChat`, `_resetSse`, `_resetEventLog`). The earlier
 * shared `_reset` name caused "spread later wins" silent overrides — fixed
 * by the per-slice rename. Slice ordering in the composition below is still
 * preserved as documented for future maintainers, but it is no longer
 * load-bearing for reset correctness.
 */
export const useRootStore = create<RootStore>()((...a) => ({
  ...createNavigationSlice(...a),
  ...createChatSlice({
    attachInlineCardDelegate: (messageId: string, envelope: InlineCardEnvelope): number =>
      useRootStore.getState().attachCard(messageId, envelope),
    // IV3-CARD — `loadTranscript` rehydrates persisted `metadata.images[]`
    // into the image-views slice (keyed by `imageViewId`) so an LLM-raster
    // card survives a reload on the producing turn. Bound via a closure over
    // `getState` for the same TDZ reason as `attachInlineCardDelegate`.
    rehydrateImageFromManifest: (args): void =>
      useRootStore.getState().rehydrateFromManifest(args),
  })(...a),
  ...createSseSlice(...a),
  ...createInlineCardsSlice()(...a),
  ...createChangesetSlice()(...a),
  ...createViewsSlice()(...a),
  ...createKbSlice()(...a),
  ...createDiffSlice()(...a),
  ...createEventLogSlice(...a),
  ...createGitSlice()(...a),
  ...createImageViewsSlice()(...a),
}));

// W2-S4 AC #3 — hydrate persisted tabs before the first render. `restoreTabs`
// is idempotent (private flag inside the slice creator).
useRootStore.getState().restoreTabs();

/**
 * Test-only helper — resets the root store's slice state to defaults. Action
 * functions are preserved (they close over `useRootStore.setState`). Call
 * this in test `beforeEach` blocks to avoid cross-test pollution.
 *
 * MED-2 polish — five composed slices ship per-slice prefixed reset actions
 * (`_resetInlineCards`, `_resetChangeset`, `_resetViews`, `_resetKb`,
 * `_resetDiff`) instead of the pre-rename collision-prone `_reset` name.
 * This helper still resets via `*_INITIAL_STATE` spread rather than calling
 * the actions: the spread skips slice-local side effects (e.g. `kb._resetKb`
 * aborts inflight controllers) that aren't needed in `beforeEach` and that
 * could leak across tests if the active controllers belong to a different
 * test scope.
 */
export function _resetRootStoreForTests(): void {
  useRootStore.setState({
    ...NAVIGATION_INITIAL_STATE,
    ...CHAT_INITIAL_STATE,
    ...SSE_INITIAL_STATE,
    ...INLINE_CARDS_INITIAL_STATE,
    ...CHANGESET_INITIAL_STATE,
    ...VIEWS_INITIAL_STATE,
    ...KB_INITIAL_STATE,
    ...DIFF_INITIAL_STATE,
    ...EVENT_LOG_INITIAL_STATE,
    ...GIT_INITIAL_STATE,
    ...IMAGE_VIEWS_INITIAL_STATE,
  });
  _resetTabsPersistForTests();
}
